import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { PATHS } from "@/api/constants";

type ResetFormPayload = {
  password: string;
  confirmPassword: string;
};

export default function useResetPassword(tokenFromUrl?: string | null) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResetPassword = useCallback(
    async ({ password, confirmPassword }: ResetFormPayload) => {
      if (loading) return; // ⛔ prevent double submit

      const token = (tokenFromUrl ?? "").trim();

      if (!token) {
        setError("Invalid or expired reset link.");
        setSuccess(false);
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const res = await api.post(PATHS.auth.resetPassword, {
          token,
          newPassword: password,
          confirmPassword,
        });

        if (res.data?.ok) {
          setSuccess(true);

          // Redirect after success
          setTimeout(() => {
            navigate(PATHS.auth.login);
          }, 800);
        } else {
          setError(
            res.data?.message || "Unable to reset password. Please try again."
          );
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Unable to reset password. Please try again.";
        setError(String(msg));
      } finally {
        setLoading(false);
      }
    },
    [tokenFromUrl, navigate, loading]
  );

  return {
    loading,      // UI uses this to disable button
    success,
    error,
    handleResetPassword,
  };
}
